/* eslint-disable no-nested-ternary */
import {
  useCallback,
  useMemo,
  useState,
} from 'react';
import { Alert, Snackbar } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../store/hooks';
import {
  ResetTopTradeContentList,
  selectTopTradeContentPageNumber,
  selectGetTopTradeContentState,
  selectTopTradeHasMoreContent,
  selectTopTradeContentList,
  UpdateTopTradeHasMoreContent,
  UpdateTopTradePageNumber,
} from '../../../store/slices/contentSlice';
import { getContentByTopTrade } from '../../../store/thunks/contentThunk';
import ContentHomeSkeleton from './ContentHomeSkeleton';
import { ContentCategoryContainer } from './ContentCategoryContainer';
import { useTrackCategoryContentVisibility } from './useTrackCategoryContentVisibility';
import { useFetchCategoryContent } from './useFetchCategoryContent';
import { Content, ContentFeedSection } from '../../../common/models/content/Content';
import ContentViewMoreContainer from './ContentViewMoreContainer';
import ContentViewMoreSkeleton from './ContentViewMoreSkeleton';

interface TopTradeContentCategoryProps {
  categoryId: string;
  topTrade: string
  keyProp: string;
  setSomeContentLoadedInitially: (value: boolean) => void;
  contentFeedSection: ContentFeedSection
  viewMorePage?: boolean
}
export function TopTradeContentCategory({
  categoryId,
  topTrade,
  keyProp,
  viewMorePage = false,
  setSomeContentLoadedInitially,
  contentFeedSection,
}: TopTradeContentCategoryProps) {
  const storeTopTradeContentList = useAppSelector(selectTopTradeContentList);
  const topTradeContentList = useMemo(() => storeTopTradeContentList.map((content, indx) => (
    {
      ...content,
      contentFeedSection,
      contentFeedSectionPosition: indx + 1,
    } as Content
  )), [storeTopTradeContentList, contentFeedSection]);

  const getTopTradeContentState = useAppSelector(selectGetTopTradeContentState);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const { t } = useTranslation();
  const topTradePage = useAppSelector(selectTopTradeContentPageNumber);
  const topTradeHasMore = useAppSelector(selectTopTradeHasMoreContent);
  const [initialFetchDone, setInitialFetchDone] = useState(false);

  useTrackCategoryContentVisibility({
    categoryContentList: topTradeContentList,
  });

  const fetchMoreCategoryContent = useFetchCategoryContent({
    viewMorePage,
    contentRequestOptions: { businessTrades: topTrade },
    getCategoryContent: getContentByTopTrade,
    resetCategoryContentList: ResetTopTradeContentList,
    setSomeContentLoadedInitially,
    setOpenErrorSnackbar,
    setInitialFetchDone,
    updateCategoryPageNumber: UpdateTopTradePageNumber,
    updateHasMoreCategoryContent: UpdateTopTradeHasMoreContent,
  });

  const isLoading = useMemo(
    () => (getTopTradeContentState === 'pending' && !initialFetchDone),
    [getTopTradeContentState, initialFetchDone],
  );

  const handleCloseErrorSnackBar = () => {
    setOpenErrorSnackbar(false);
  };

  const onNextPage = useCallback(() => {
    fetchMoreCategoryContent(
      topTradePage,
      topTradeHasMore,
    );
  }, [topTradePage, topTradeHasMore, fetchMoreCategoryContent]);

  return (
    <>
      <Snackbar open={openErrorSnackbar} autoHideDuration={4000} onClose={handleCloseErrorSnackBar}>
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('error.generalMessage')}
        </Alert>
      </Snackbar>

      {isLoading ? (
        viewMorePage ? (
          <ContentViewMoreSkeleton />
        ) : (
          <ContentHomeSkeleton key={`${keyProp}-skeleton`} />
        )
      ) : viewMorePage ? (
        <ContentViewMoreContainer
          key={`${keyProp}-content`}
          categoryId={categoryId}
          topTradeName={topTrade}
          storeContentList={topTradeContentList}
          onNextPage={onNextPage}
          hasMoreContent={topTradeHasMore}
          showSearchBar={true}
        />
      ) : topTradeContentList?.length > 0 ? (
        <ContentCategoryContainer
          categoryId={categoryId}
          key={`${keyProp}-content`}
          categorySubType={topTrade}
          categoryTitle={`Latest in ${topTrade}`}
          storeContentList={topTradeContentList}
          contentFeedSection={contentFeedSection}
        />
      ) : null}
    </>
  );
}
